// Browser-based kitchen ticket ("comanda") printing. Opens a lightweight
// popup styled as an 80mm receipt and triggers the OS print dialog — works
// with any printer, including thermal, with no driver/hardware integration.
//
// Variants:
//   'kitchen' → cooking ticket: big items + notes, no prices/payment
//   'counter' → hand-off ticket: customer/address/payment/total, compact items
//   'full'    → everything (default)

function esc(s) {
  return String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
}

export function buildComandaHtml(order, variant = 'full') {
  const isDelivery = order.order_type === 'delivery';
  const created = order.created_at ? new Date(order.created_at) : new Date();
  const time = created.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const date = created.toLocaleDateString('it-IT');
  const eta = order.eta_at ? new Date(order.eta_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
  const addr = order.delivery_address || {};
  const payMap = { cod: 'Contanti alla consegna/ritiro', stripe: 'Pagato (carta)', paypal: 'Pagato (PayPal)', points: 'Pagato con punti' };

  const showItems = variant !== 'counter';
  const showCommercial = variant !== 'kitchen';

  const items = (order.items || []).map(it => {
    const mods = (it.modifiers || []).map(m => `<div class="mod">+ ${esc(m.name || m)}</div>`).join('');
    return `<div class="row"><span class="qty">${esc(it.quantity)}x</span><span class="name">${esc(it.name)}</span></div>${mods}`;
  }).join('');

  const compactItems = (order.items || []).map(it => `<div class="crow">${esc(it.quantity)}× ${esc(it.name)}</div>`).join('');

  const heading = variant === 'kitchen' ? 'CUCINA' : (variant === 'counter' ? (isDelivery ? 'CONSEGNA' : 'BANCO') : (isDelivery ? 'DELIVERY' : 'RITIRO'));

  return `<!doctype html><html><head><meta charset="utf-8"><title>Comanda ${esc(order.order_number)}</title>
  <style>
    @page { size: 80mm auto; margin: 3mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; width: 74mm; margin: 0 auto; color: #000; }
    .center { text-align: center; }
    .big { font-size: 20px; font-weight: 800; }
    .huge { font-size: 26px; font-weight: 800; }
    .muted { font-size: 11px; }
    hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
    .row { display: flex; font-size: 15px; font-weight: 700; margin: 3px 0; }
    .qty { min-width: 30px; }
    .name { flex: 1; }
    .mod { font-size: 12px; margin-left: 30px; }
    .crow { font-size: 12px; }
    .tag { display:inline-block; border:2px solid #000; padding:2px 8px; font-weight:800; font-size:16px; margin-top:4px;}
    .notes { border:1px solid #000; padding:4px; font-size:13px; margin-top:6px; }
  </style></head><body>
    <div class="center">
      <div class="big">${heading}</div>
      <div class="huge">#${esc(order.order_number)}</div>
      <div class="muted">${esc(date)} ${esc(time)}</div>
      ${eta ? `<div class="tag">${isDelivery ? 'Consegna' : 'Pronto'} ~ ${esc(eta)}</div>` : ''}
    </div>
    <hr>
    ${showItems ? items : compactItems}
    ${order.notes ? `<hr><div class="notes">NOTE: ${esc(order.notes)}</div>` : ''}
    ${showCommercial ? `
      <hr>
      ${order.customer_name ? `<div class="muted">Cliente: ${esc(order.customer_name)}</div>` : ''}
      ${order.customer_phone ? `<div class="muted">Tel: ${esc(order.customer_phone)}</div>` : ''}
      ${isDelivery ? `<div class="muted">Indirizzo: ${esc([addr.street, addr.city, addr.zip].filter(Boolean).join(', '))}</div>` : ''}
      <div class="muted">${esc(payMap[order.payment_method] || order.payment_method || '')}</div>
      <div class="muted">Totale: ${Number(order.total || 0).toFixed(2)} EUR</div>
    ` : ''}
  </body></html>`;
}

function _printOne(order, variant) {
  const html = buildComandaHtml(order, variant);
  const w = window.open('', `comanda_${variant}`, 'width=380,height=640');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 300);
}

export function printComanda(order, mode = 'full') {
  try {
    if (mode === 'both') {
      _printOne(order, 'kitchen');
      setTimeout(() => _printOne(order, 'counter'), 500);
    } else {
      _printOne(order, mode);
    }
  } catch (e) {
    // best-effort
  }
}
